import { Platform, Share } from 'react-native';
import * as Sharing from 'expo-sharing';
import i18n from 'i18next';
import { getAPIBaseURL } from './api-url';
import { appFetch, getAuthorizedHeaders } from './app-auth';
import { readAPIResponse } from './api';
import type { CvExportFormat } from './cv-export-policy';

export type ExportFormat = CvExportFormat;

export interface CvDownloadOptions {
  target?: string | null;
  variantId?: string | null;
  revisionId?: string | null;
  releaseId?: string | null;
  locale?: string | null;
  direction?: 'ltr' | 'rtl' | null;
  visibility?: {
    showEmail?: boolean;
    showPhone?: boolean;
    showLocation?: boolean;
    showLinks?: boolean;
    showPhoto?: boolean;
  };
}

const mimeMap: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  md: 'text/markdown',
};

export const DOWNLOAD_FAILED_MESSAGE = 'Download failed. Please try again.';

/**
 * Fill the export `locale` from the active app language when the caller didn't
 * specify one, so a CV viewed in an Arabic app downloads with Arabic headings +
 * RTL to match the on-screen WebCV (issue #80). It's only a hint: the server
 * still upgrades to Arabic for Arabic-authored content, and an explicit
 * caller-supplied locale is left untouched. Covers every download path (share,
 * tailored, chat tool-cards, onboarding) since it runs at the download entry.
 */
export function withAppLocaleDefault(
  options?: CvDownloadOptions,
): CvDownloadOptions {
  const opts = options ?? {};
  if (opts.locale) return opts;
  const lang = typeof i18n.language === 'string' ? i18n.language : '';
  return { ...opts, locale: lang.toLowerCase().startsWith('ar') ? 'ar' : 'en' };
}

/**
 * Parse the filename from a Content-Disposition header, with RFC 5987 support.
 */
export function parseContentDispositionFilename(
  header: string | null,
  fallback: string,
): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through */ }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare) return bare[1].trim();
  return fallback;
}

export async function getExportFailureMessage(
  response: Response,
  fallback = DOWNLOAD_FAILED_MESSAGE,
): Promise<string> {
  const envelope = await readAPIResponse<never>(response);
  return envelope.error?.message || fallback;
}

/**
 * Download a file to a temp path, rename it to the server-provided filename,
 * and open the system share sheet. Native-only.
 */
export async function downloadAndShareNative(
  url: string,
  format: ExportFormat,
  defaultName: string,
): Promise<void> {
  const { downloadAsync, documentDirectory, moveAsync } = require('expo-file-system/legacy');

  const tempUri = documentDirectory + `cv-export-tmp.${format}`;
  const downloadResult = await downloadAsync(url, tempUri, {
    headers: await getAuthorizedHeaders(),
  });

  if (downloadResult.status !== 200) {
    throw new Error('Export failed');
  }

  const headerFilename = downloadResult.headers?.['content-disposition']
    ?? downloadResult.headers?.['Content-Disposition'];
  const filename = parseContentDispositionFilename(headerFilename ?? null, defaultName);

  const finalUri = documentDirectory + filename;
  let targetUri = tempUri;
  if (finalUri !== tempUri) {
    try {
      await moveAsync({ from: tempUri, to: finalUri });
      targetUri = finalUri;
    } catch { /* fall back to temp uri */ }
  }

  const canShare = await Sharing.isAvailableAsync();
  if (canShare) {
    await Sharing.shareAsync(targetUri, {
      mimeType: mimeMap[format] ?? 'application/octet-stream',
      dialogTitle: filename,
      UTI: format === 'pdf' ? 'com.adobe.pdf' : undefined,
    });
  } else {
    await Share.share({ url: targetUri });
  }
}

/**
 * Download a CV export in the specified format.
 * Web: triggers a browser download via blob URL.
 * Native: downloads to temp file and opens the system share sheet.
 */
export async function downloadCvExport(
  format: ExportFormat,
  options?: CvDownloadOptions,
): Promise<void> {
  const resolved = withAppLocaleDefault(options);
  if (Platform.OS === 'web') {
    await downloadWeb(format, resolved);
  } else {
    await downloadNative(format, resolved);
  }
}

// ─── Web: blob download ───────────────────────────────────────

async function downloadWeb(format: ExportFormat, options?: CvDownloadOptions): Promise<void> {
  const url = `${getAPIBaseURL()}/api/cv/export/${format}${buildExportQuery(options)}`;
  let response: Response;
  try {
    response = await appFetch(url, {
      method: 'GET',
    });
  } catch {
    throw new Error(DOWNLOAD_FAILED_MESSAGE);
  }

  if (!response.ok) {
    throw new Error(await getExportFailureMessage(response));
  }
  const filename = parseContentDispositionFilename(
    response.headers.get('content-disposition'),
    `cv.${format}`,
  );
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

// ─── Native: file download + system share sheet ───────────────

async function downloadNative(format: ExportFormat, options?: CvDownloadOptions): Promise<void> {
  const url = `${getAPIBaseURL()}/api/cv/export/${format}${buildExportQuery(options)}`;
  await downloadAndShareNative(url, format, `cv.${format}`);
}

export function buildExportQuery(options?: CvDownloadOptions): string {
  if (!options) return '';
  const params = new URLSearchParams();
  if (options.target) params.set('target', options.target);
  if (options.variantId) params.set('variantId', options.variantId);
  if (options.revisionId) params.set('revisionId', options.revisionId);
  if (options.releaseId) params.set('releaseId', options.releaseId);
  if (options.locale) params.set('locale', options.locale);
  if (options.direction) params.set('direction', options.direction);
  const visibility = options.visibility ?? {};
  for (const [key, value] of Object.entries(visibility)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}
